import { useMemo, useState } from "react";
import { ProgressBar } from "@/components/ProgressBar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { GoalsScreen } from "@/screens/GoalsScreen";
import { TimeHorizonScreen } from "@/screens/TimeHorizonScreen";
import { PersonaScreen } from "@/screens/PersonaScreen";
import { AnalysisScreen } from "@/screens/AnalysisScreen";
import { ResultScreen } from "@/screens/ResultScreen";
import { calculateRetirement } from "@/utils/calculations";

const STEPS = {
  GOALS: "goals",
  TIME: "time",
  PERSONA: "persona",
  ANALYSIS: "analysis",
  RESULT: "result",
};

export const Wizard = () => {
  const [step, setStep] = useState(STEPS.GOALS);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [timeHorizon, setTimeHorizon] = useState(null);
  const [persona, setPersona] = useState(null);

  const toggleGoal = (goal) => {
    setSelectedGoals((prev) => {
      const already = prev.some((g) => g.id === goal.id);
      if (already) {
        return prev.filter((g) => g.id !== goal.id);
      }
      // Enforce group exclusivity — only one goal per group can be selected
      const filtered = goal.group
        ? prev.filter((g) => g.group !== goal.group)
        : prev;
      return [...filtered, goal];
    });
  };

  const result = useMemo(() => {
    if (!timeHorizon || selectedGoals.length === 0) return null;
    return calculateRetirement(selectedGoals, timeHorizon.years);
  }, [selectedGoals, timeHorizon]);

  const stepNum =
    step === STEPS.GOALS
      ? 1
      : step === STEPS.TIME
      ? 2
      : step === STEPS.PERSONA
      ? 3
      : 3;

  const restart = () => {
    setSelectedGoals([]);
    setTimeHorizon(null);
    setPersona(null);
    setStep(STEPS.GOALS);
  };

  const showChrome = step !== STEPS.ANALYSIS;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50 font-body antialiased relative overflow-x-hidden transition-colors duration-300">
      {/* Background texture */}
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-[0.6] dark:opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 0%, rgb(var(--brand-glow) / 0.06), transparent 40%), radial-gradient(circle at 80% 100%, rgba(0,0,0,0.04), transparent 50%)",
        }}
      />
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-[0.03] dark:opacity-[0.04]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {step !== STEPS.ANALYSIS && step !== STEPS.RESULT && (
        <ProgressBar step={stepNum} total={3} />
      )}

      {/* Header: logo sits in normal document flow (no overlap on scroll) */}
      {showChrome && (
        <header className="w-full flex items-center justify-center pt-6 md:pt-8 lg:pt-10 pb-1">
          <Logo />
        </header>
      )}

      {/* Theme toggle stays fixed top-right (small floating control) */}
      {showChrome && (
        <div className="fixed top-4 right-4 md:top-5 md:right-6 z-40">
          <ThemeToggle />
        </div>
      )}

      {step === STEPS.GOALS && (
        <GoalsScreen
          selected={selectedGoals}
          onToggle={toggleGoal}
          onContinue={() => setStep(STEPS.TIME)}
        />
      )}

      {step === STEPS.TIME && (
        <TimeHorizonScreen
          selected={timeHorizon}
          onSelect={setTimeHorizon}
          onContinue={() => setStep(STEPS.PERSONA)}
          onBack={() => setStep(STEPS.GOALS)}
        />
      )}

      {step === STEPS.PERSONA && (
        <PersonaScreen
          selected={persona}
          onSelect={setPersona}
          onContinue={() => setStep(STEPS.ANALYSIS)}
          onBack={() => setStep(STEPS.TIME)}
        />
      )}

      {step === STEPS.ANALYSIS && result && (
        <AnalysisScreen
          result={result}
          selectedIcons={selectedGoals.map((g) => g.icon)}
          onDone={() => setStep(STEPS.RESULT)}
        />
      )}

      {step === STEPS.RESULT && result && (
        <ResultScreen
          result={result}
          selectedGoals={selectedGoals}
          persona={persona}
          onRestart={restart}
        />
      )}
    </div>
  );
};
